import { Button, CrashIcon, EmptyState } from '@lmc/ui-kit'
import * as Sentry from '@sentry/react'
import type { SentryFeatureArea } from 'common/services/sentry'
import type React from 'react'
import styled from 'styled-components'

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`

const isStaleChunkError = (error: unknown) => {
  return (
    error instanceof Error &&
    (error.message.includes('Loading chunk') ||
      error.message.includes('dynamically imported module') ||
      error.message.includes('Content-Type'))
  )
}

type ErrorFallbackProps = {
  error: Error
  resetError: () => void
}

function ErrorFallback({ error, resetError }: ErrorFallbackProps) {
  const refreshPage = () => {
    window.location.reload()
  }

  if (isStaleChunkError(error)) {
    return (
      <ErrorContainer>
        <EmptyState
          title="The Care Office was updated"
          description="Please refresh the page to get the latest version"
          icon={<CrashIcon />}
        />
        <Button variant="primary" onClick={refreshPage}>
          Refresh now
        </Button>
      </ErrorContainer>
    )
  }

  return (
    <ErrorContainer>
      <EmptyState
        title="Whoops! There was a crash..."
        description="Don't worry, try refreshing the page and if the problem persists contact support."
        icon={<CrashIcon />}
      />
      <Button variant="primary" onClick={refreshPage}>
        Refresh page
      </Button>
    </ErrorContainer>
  )
}

type ErrorBoundaryProps = {
  children: React.ReactNode
  /**
   * Tags errors caught here with a Sentry `area`, so widgets rendered outside
   * their feature's own routes (e.g. an emar/rota home dashboard card) still
   * get attributed correctly instead of falling back to the current route's
   * area — see getFeatureAreaFromPathname in common/services/sentry.ts.
   */
  area?: SentryFeatureArea
  /** Renders nothing on error instead of the crash card — for widgets that
   * are normally invisible (e.g. a modal that hasn't been opened). */
  silent?: boolean
}

const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({
  children,
  area,
  silent
}) => (
  <Sentry.ErrorBoundary
    fallback={
      silent ? (
        <></>
      ) : (
        ({ error, resetError }) => (
          <ErrorFallback error={error} resetError={resetError} />
        )
      )
    }
    showDialog={false}
    beforeCapture={(scope) => {
      if (area) scope.setTag('area', area)
    }}
  >
    {children}
  </Sentry.ErrorBoundary>
)

export default ErrorBoundary
